/**
 * IndexedDB хранилище для исходных изображений генераций.
 * Заменяет base64 в localStorage — нет лимита 5MB, хранит бинарный File.
 */

const DB_NAME = 'umnik_source_images'
const STORE_NAME = 'images'
const DB_VERSION = 1

let cachedDb: IDBDatabase | null = null

function openDB(): Promise<IDBDatabase> {
	if (cachedDb) return Promise.resolve(cachedDb)

	return new Promise((resolve, reject) => {
		const req = indexedDB.open(DB_NAME, DB_VERSION)
		req.onupgradeneeded = (e) => {
			const db = (e.target as IDBOpenDBRequest).result
			if (!db.objectStoreNames.contains(STORE_NAME)) {
				db.createObjectStore(STORE_NAME)
			}
		}
		req.onsuccess = () => {
			cachedDb = req.result
			cachedDb.onclose = () => { cachedDb = null }
			cachedDb.onversionchange = () => { cachedDb?.close(); cachedDb = null }
			resolve(cachedDb)
		}
		req.onerror = () => reject(req.error)
	})
}

export async function saveSourceImage(key: string, file: File): Promise<void> {
	const db = await openDB()
	return new Promise((resolve, reject) => {
		const tx = db.transaction(STORE_NAME, 'readwrite')
		// Сохраняем метаданные рядом с бинарными данными,
		// т.к. File может десериализоваться как Blob без name/type
		const blob = file.slice(0, file.size, file.type)
		tx.objectStore(STORE_NAME).put({ blob, name: file.name, type: file.type }, key)
		tx.oncomplete = () => resolve()
		tx.onerror = () => reject(tx.error)
	})
}

export async function getSourceImage(key: string): Promise<File | null> {
	const db = await openDB()
	return new Promise((resolve, reject) => {
		const tx = db.transaction(STORE_NAME, 'readonly')
		const req = tx.objectStore(STORE_NAME).get(key)
		req.onsuccess = () => {
			const result = req.result
			if (!result) return resolve(null)

			// Новый формат: { blob, name, type }
			if (result.blob) {
				resolve(new File([result.blob], result.name || 'source', { type: result.type || result.blob.type }))
				return
			}

			// Legacy: File/Blob напрямую
			if (result instanceof Blob) {
				resolve(new File([result], 'source', { type: result.type }))
				return
			}

			resolve(null)
		}
		req.onerror = () => reject(req.error)
	})
}

export async function deleteSourceImage(key: string): Promise<void> {
	try {
		const db = await openDB()
		await new Promise<void>((resolve) => {
			const tx = db.transaction(STORE_NAME, 'readwrite')
			tx.objectStore(STORE_NAME).delete(key)
			tx.oncomplete = () => resolve()
			tx.onerror = () => resolve()
		})
	} catch {
		// тихо игнорируем
	}
}

export async function deleteSourceImages(keys: string[]): Promise<void> {
	if (keys.length === 0) return
	try {
		const db = await openDB()
		await new Promise<void>((resolve) => {
			const tx = db.transaction(STORE_NAME, 'readwrite')
			const store = tx.objectStore(STORE_NAME)
			keys.forEach((k) => store.delete(k))
			tx.oncomplete = () => resolve()
			tx.onerror = () => resolve()
		})
	} catch {
		// тихо игнорируем
	}
}
